import { Link } from 'react-router-dom'
import { ModalShell } from './ModalShell'
import { useClientDetail } from './useDrilldown'
import { requestStatusLabel } from '../../../../shared/ui/Badge'
import type { RequestStatus } from '../../../../shared/api/types'

function formatRub(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `₽ ${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `₽ ${Math.round(n / 1_000)}K`
  return `₽ ${Math.round(n)}`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

export function ClientDetailModal({
  clientId,
  onClose,
}: {
  clientId: string
  onClose: () => void
}) {
  const { data, isLoading, isError } = useClientDetail(clientId)

  return (
    <ModalShell
      title={data?.name ?? 'Клиент'}
      subtitle={
        data
          ? [
              data.category && `Категория ${data.category}`,
              data.city,
              data.inn && `ИНН ${data.inn}`,
              data.manager?.name && `Менеджер: ${data.manager.name}`,
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить данные</p>}
      {data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Заявок" value={data.stats.requestsCount} />
            <Stat label="Сумма КП" value={formatRub(data.stats.kpSumRub)} />
            <Stat
              label="Последняя активность"
              value={
                data.stats.lastActivityAt === null ? '—' : formatDate(data.stats.lastActivityAt)
              }
            />
          </div>

          <div className="flex justify-end">
            <Link
              to={`/clients/${data.id}`}
              onClick={onClose}
              className="text-sm text-brand-600 hover:underline"
            >
              Открыть карточку клиента →
            </Link>
          </div>

          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Последние заявки
            </div>
            {data.recentRequests.length === 0 ? (
              <p className="text-sm text-gray-400">Нет заявок</p>
            ) : (
              <div className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                {data.recentRequests.slice(0, 10).map((r) => (
                  <Link
                    key={r.id}
                    to={`/requests/${r.id}`}
                    onClick={onClose}
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    <span className="font-mono text-xs text-gray-500 w-16 shrink-0">
                      {r.requestNumber ?? '—'}
                    </span>
                    <span className="flex-1 text-gray-800">
                      {requestStatusLabel(r.status as RequestStatus)}
                    </span>
                    <span className="text-xs text-gray-500 tabular-nums shrink-0">
                      {formatDate(r.createdAt)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  )
}
